import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {Session} from "../../interfaces/session-data";
import {NavigationEnd, Router} from "@angular/router";
import {filter, map} from "rxjs/operators";

export interface AnalyticsEvent {
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  sessionToken?: string;
  merchantReference?: string;
  merchantLabel?: string;
  amount?: string;
  paymentMethod?: string;
  eventComponentName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
//   private baseUrl = environment.baseUrl || '';
    private analyticsEndpoint =  environment.production ? "/channel-virtual/api/v1" + '/analytics' : environment.baseUrl + '/analytics';


  private currentComponentName: string = '';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Subscribe to router events to track the current component
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.router.routerState.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary')
    ).subscribe(route => {
      // Get component name from route data or component instance
      if (route.component) {
        this.currentComponentName = route.component['name'] || route.component.toString().split('(')[0].split(' ')[1];
      }
    });
  }

  /**
   * Get the current component name
   * @returns The name of the current component
   */
  public getCurrentComponentName(): string {
    return this.currentComponentName;
  }

  /**
   * Track a custom event using sendBeacon (preferred) or fallback to HTTP
   * @param event The analytics event to track
   */
  public trackEvent(event: AnalyticsEvent): Observable<void> {
    console.log('Tracking event:', event);
    if (this.sendBeaconEvent(event)) {
      return of(void 0); // Return observable for consistent API
    } else {
    //   Fallback to regular HTTP post if sendBeacon fails or isn't supported
      return this.http.post<void>(`${this.analyticsEndpoint}/track`, event);
    }
  }

  /**
   * Track a page view using sendBeacon (preferred) or fallback to HTTP
   * @param event The analytics event with page details
   */
  public trackPageView(event: AnalyticsEvent): Observable<void> {
    console.log('Tracking page view:', event);
    if (this.sendBeaconPageView(event)) {
      return of(void 0); // Return observable for consistent API
    } else {
      // Fallback to regular HTTP post if sendBeacon fails or isn't supported
      return this.http.post<void>(`${this.analyticsEndpoint}/page-view`, event);
    }
  }

  /**
   * Sends an analytics event using navigator.sendBeacon
   * @param event The analytics event to send
   * @returns boolean indicating if sendBeacon was successfully queued
   */
  private sendBeaconEvent(event: AnalyticsEvent): boolean {
    try {
      if (navigator && navigator.sendBeacon) {
        // Use GET URL approach - simplest and most cross-compatible
        const params = new URLSearchParams();
        params.append('eventType', 'custom');
        params.append('eventCategory', event.eventCategory);
        params.append('eventAction', event.eventAction);

        if (event.eventLabel) params.append('eventLabel', event.eventLabel);
        if (event.sessionToken) params.append('sessionToken', event.sessionToken);

        // Add additional properties from the AnalyticsEvent interface
        if (event.merchantReference) params.append('merchantReference', event.merchantReference);
        if (event.merchantLabel) params.append('merchantLabel', event.merchantLabel);
        if (event.amount) params.append('amount', event.amount.toString());
        if (event.paymentMethod) params.append('paymentMethod', event.paymentMethod);
        if (event.eventComponentName) params.append('componentName', event.eventComponentName);

        // Add current component name if not explicitly provided
        if (!event.eventComponentName && this.currentComponentName) {
          params.append('componentName', this.currentComponentName);
        }

        // Use a simple empty string for the blob to avoid CORS preflight
        return navigator.sendBeacon(`${this.analyticsEndpoint}/beacon?${params.toString()}`, '');
      }
    } catch (error) {
      console.warn('Beacon request failed:', error);
    }
    return false;
  }

  /**
   * Sends a page view event using the beacon API
   * @param event The analytics event to send
   * @returns boolean indicating if sendBeacon was successfully queued
   */
  private sendBeaconPageView(event: AnalyticsEvent): boolean {
    try {
      if (navigator && navigator.sendBeacon) {
        const params = new URLSearchParams();
        params.append('page', event.eventAction);

        if (event.eventLabel) params.append('referrer', event.eventLabel);

        // Use empty string to avoid CORS preflight
        return navigator.sendBeacon(`${this.analyticsEndpoint}/beacon/page-view?${params.toString()}`, '');
      }
    } catch (error) {
      console.warn('Beacon page view request failed:', error);
    }
    return false;
  }

  /**
   * Alternative method to track events using image pixels (works even with auth restrictions)
   * @param params URLSearchParams object with event data
   */
  private trackWithPixel(params: URLSearchParams): void {
    // Add pixel param to request a GIF response and random nonce to prevent caching
    params.append('pixel', 'true');
    params.append('nonce', Date.now().toString());

    const img = new Image();
    img.src = `${this.analyticsEndpoint}/beacon?${params.toString()}`;
  }

  /**
   * Track page load event
   * @param sessionToken
   * @param session
   * @param location The URL the client has loaded
   * @param eventComponentName
   */
  public trackPageLoad(sessionToken: string, session: Session, location: string, eventComponentName: string): Observable<void> {
    const event: AnalyticsEvent = {
      eventCategory: 'Page Load',
      eventAction: location,
      sessionToken,
      merchantLabel: session.sessionDisplayInformation.label,
      merchantReference: session.sessionDisplayInformation.merchantReference,
      amount: session.sessionDisplayInformation.currencyCode+" "+session.sessionDisplayInformation.amount,
      eventComponentName
    };
    return this.trackEvent(event);
  }

  /**
   * Track payment method selection
   * @param sessionToken The session token
   * @param paymentMethod The selected payment method
   */
  public trackPaymentMethodSelection(sessionToken: string, paymentMethod: string): Observable<void> {
    const event: AnalyticsEvent = {
      eventCategory: 'Payment',
      eventAction: 'MethodSelection',
      eventLabel: paymentMethod,
      sessionToken,
      paymentMethod
    };
    return this.trackEvent(event);
  }

  /**
   * Track payment completion
   * @param sessionToken The session token
   * @param paymentMethod The payment method used
   * @param success Whether the payment was successful
   */
  public trackPaymentCompletion(sessionToken: string, paymentMethod: string, success: boolean): Observable<void> {
    const event: AnalyticsEvent = {
      eventCategory: 'Payment',
      eventAction: 'Completion',
      eventLabel: success ? 'Success' : 'Failure',
      sessionToken,
      paymentMethod
    };
    return this.trackEvent(event);
  }

  /**
   * Track page unload event
   * @param sessionToken The session token
   * @param session The current session data
   * @param location The URL the client is unloading from
   * @param eventComponentName
   */
  public trackPageUnload(sessionToken: string, session: Session, location: string, eventComponentName: string): Observable<void> {
    const event: AnalyticsEvent = {
      eventCategory: 'Page Unload',
      eventAction: location || window.location.pathname,
      eventLabel: 'UserInitiated',
      sessionToken,
      eventComponentName
    };
    if (session && session.sessionDisplayInformation) {
      event.merchantLabel = session.sessionDisplayInformation.label;
      event.merchantReference = session.sessionDisplayInformation.merchantReference;
      event.amount = session.sessionDisplayInformation.currencyCode + " " + session.sessionDisplayInformation.amount;
    }
    this.sendBeaconEvent(event);
    // // For page unload, try both beacon and pixel approaches for maximum reliability
    // try {
    //   if (navigator && navigator.sendBeacon) {
    //    this.sendBeaconEvent(event);
    //   }
    // } catch (error) {
    //   console.warn('Page unload beacon failed:', error);
    // }
    // // this.trackWithPixel(params);
    return of(void 0);
  }
}
